import {
  useMemo,
  useState,
} from "react";

import {
  Link,
} from "react-router-dom";

import {
  Button,
  PageContainer,
  type ComponentDiagnostic,
} from "@swot/base-components";

import type {
  EnterpriseNotification,
  EnterpriseTask,
} from "@swot/swot-components";

import {
  CmtsSmartDataGrid,
  type CmtsGridColumn,
} from "@components/data-grid/index.js";

import {
  CmtsPageHintPanel,
} from "@components/hints/index.js";

import {
  CmtsEnterpriseModal,
} from "@components/CmtsEnterpriseModal.js";

type WorkRecord =
  Record<
    string,
    unknown
  >;

type ComplaintOption = {
  readonly value: string;
  readonly label: string;
};

type CmtsMyWorkspacePageProps = {
  readonly tasks?:
    | readonly EnterpriseTask[]
    | undefined;

  readonly notifications?:
    | readonly EnterpriseNotification[]
    | undefined;

  readonly messages?:
    | readonly WorkRecord[]
    | undefined;

  readonly userId?:
    | string
    | undefined;

  readonly complaintOptions?:
    | readonly ComplaintOption[]
    | undefined;

  readonly diagnostics?:
    | readonly ComponentDiagnostic[]
    | undefined;

  readonly busy?:
    | boolean
    | undefined;

  readonly onOpenTask:
    (
      task:
        EnterpriseTask,
    ) =>
      void |
      Promise<void>;

  readonly onCompleteTask:
    (
      task:
        EnterpriseTask,
    ) =>
      void |
      Promise<void>;

  readonly onMarkRead:
    (
      notification:
        EnterpriseNotification,
    ) =>
      void |
      Promise<void>;

  readonly onOpenNotification:
    (
      notification:
        EnterpriseNotification,
    ) =>
      void |
      Promise<void>;

  readonly onOpenMessage:
    (
      message:
        WorkRecord,
    ) =>
      void |
      Promise<void>;

  readonly onMarkMessageRead?:
    | ((
        message:
          WorkRecord,
      ) =>
        void |
        Promise<void>)
    | undefined;

  readonly onOpenAuditTrail?:
    | ((
        row:
          WorkRecord,
      ) => void)
    | undefined;

  readonly onOpenOperationSpine?:
    | ((
        row:
          WorkRecord,
      ) => void)
    | undefined;

  readonly onRefresh:
    () =>
      void |
      Promise<void>;
};

function text(
  row:
    WorkRecord,

  keys:
    readonly string[],

  fallback = "",
): string {
  for (
    const key of keys
  ) {
    const value =
      row[key];

    if (
      value !==
        undefined &&
      value !==
        null &&
      String(value).trim()
    ) {
      return String(
        value,
      ).trim();
    }
  }

  return fallback;
}

function asWorkRecord(
  row: unknown,
): WorkRecord {
  return (
    typeof row ===
      "object" &&
    row !== null &&
    !Array.isArray(row)
  )
    ? row as WorkRecord
    : {};
}

const CMTS_GUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function isGuidValue(
  value: unknown,
): boolean {
  return CMTS_GUID_PATTERN.test(
    String(value ?? "").trim(),
  );
}

function normalizeCode(
  value: string,
): string {
  return value
    .trim()
    .toLowerCase()
    .replace(
      /[^a-z0-9]+/g,
      "-",
    )
    .replace(
      /^-+|-+$/g,
      "",
    ) || "default";
}

function displayLabel(
  value: string,
): string {
  const normalized =
    value
      .trim()
      .replace(
        /[_-]+/g,
        " ",
      );

  return normalized ||
    "Unknown";
}

function taskStatus(
  row:
    WorkRecord,
): string {
  return text(
    row,
    [
      "taskStatus",
      "notificationState",
      "readState",
      "status",
      "statusCode",
    ],
    "Open",
  );
}

function priorityValue(
  row:
    WorkRecord,
): string {
  const candidates = [
    row.priorityCode,
    row.priorityName,
    row.priorityLabel,
    row.severityName,
    row.severityCode,
    row.severity,
    row.priority,
  ];

  for (const candidate of candidates) {
    const value = String(candidate ?? "").trim();
    if (value && !isGuidValue(value)) return value;
  }

  return "Normal";
}

function moduleValue(row: WorkRecord): string {
  const workType = normalizeCode(text(row, ["workType"], ""));
  if (workType === "notification") return "Notification";
  if (workType === "message") return "Message";

  const source = normalizeCode(
    text(row, ["sourceModule", "linkedEntityType"], "Task"),
  );

  if (source.includes("rca")) return "RCA";
  if (source.includes("capa")) return "CAPA";
  if (source.includes("verification")) return "Verification";
  if (source.includes("complaint")) return "Complaint";
  return "General";
}

function isOpenTask(
  row:
    WorkRecord,
): boolean {
  const status =
    normalizeCode(
      taskStatus(row),
    );

  return ![
    "done",
    "completed",
    "closed",
    "cancelled",
    "archived",
  ].includes(status);
}

function isOverdue(
  row:
    WorkRecord,
): boolean {
  const due =
    text(
      row,
      [
        "dueAt",
        "dueAtUtc",
        "dueDate",
      ],
    );

  if (
    !due ||
    !isOpenTask(row)
  ) {
    return false;
  }

  const date =
    new Date(due);

  return (
    !Number.isNaN(
      date.getTime(),
    ) &&
    date.getTime() <
      Date.now()
  );
}

function isUnread(
  row:
    WorkRecord,
): boolean {
  const state =
    normalizeCode(
      text(
        row,
        [
          "readState",
          "readStatus",
          "notificationState",
          "status",
        ],
        "unread",
      ),
    );

  const readAt =
    text(
      row,
      [
        "readAt",
        "readAtUtc",
      ],
    );

  return (
    ![
      "read",
      "acknowledged",
      "archived",
      "sent",
    ].includes(state) &&
    !readAt
  );
}

function formatWorkDate(
  row:
    WorkRecord,
): string {
  const value =
    text(
      row,
      [
        "dueAt",
        "dueAtUtc",
        "dueDate",
        "createdAt",
        "createdAtUtc",
        "queuedAtUtc",
      ],
    );

  if (!value) {
    return "Not scheduled";
  }

  const date =
    new Date(value);

  if (
    Number.isNaN(
      date.getTime(),
    )
  ) {
    return "Date unavailable";
  }

  return new Intl
    .DateTimeFormat(
      "en-IN",
      {
        day:
          "2-digit",

        month:
          "short",

        year:
          "numeric",

        hour:
          "2-digit",

        minute:
          "2-digit",

        hour12:
          true,
      },
    )
    .format(date);
}

function linkedRecordLabel(
  row:
    WorkRecord,

  complaintOptions:
    readonly ComplaintOption[],
): string {
  const backendCode =
    text(
      row,
      [
        "linkedRecordCode",
        "complaintNumber",
        "complaintCode",
        "registrationNumber",
        "referenceNumber",
        "linkedEntityCode",
      ],
    );

  if (backendCode && !isGuidValue(backendCode)) {
    return backendCode;
  }

  const linkedId =
    text(
      row,
      [
        "complaintId",
        "parentComplaintId",
        "linkedEntityId",
        "sourceEntityId",
      ],
    );

  if (!linkedId) {
    return "No complaint linked";
  }

  const option =
    complaintOptions.find(
      (item) =>
        item.value
          .trim()
          .toLowerCase() ===
        linkedId
          .trim()
          .toLowerCase(),
    );

  if (!option) return "Complaint linked";
  return option.label.split("—")[0]?.trim() || "Complaint linked";
}

function workItemTitle(
  row:
    WorkRecord,
): string {
  return text(
    row,
    [
      "title",
      "subject",
      "body",
      "message",
      "summary",
    ],
    "CMTS work item",
  );
}

export function CmtsMyWorkspacePage(
  props:
    CmtsMyWorkspacePageProps,
) {
  const [
    actionsFor,
    setActionsFor,
  ] =
    useState<
      WorkRecord |
      null
    >(null);

  const complaintOptions =
    props.complaintOptions ??
    [];

  const taskRows =
    useMemo(
      () =>
        (
          props.tasks ??
          []
        ).map((row) => {
          const record =
            asWorkRecord(row);

          return {
            ...record,

            sourceModule:
              record.sourceModule ??
              record
                .linkedEntityType ??
              "TASK",

            workType:
              "Task",

            title:
              workItemTitle(
                record,
              ),
          };
        }),

      [props.tasks],
    );

  const notificationRows =
    useMemo(
      () =>
        (
          props.notifications ??
          []
        ).map((row) => {
          const record =
            asWorkRecord(row);

          return {
            ...record,

            sourceModule:
              record.sourceModule ??
              record
                .linkedEntityType ??
              "NOTIFICATION",

            workType:
              "Notification",

            title:
              workItemTitle(
                record,
              ),

            priority:
              record.priority ??
              record.severity ??
              "Medium",
          };
        }),

      [props.notifications],
    );

  const messageRows =
    useMemo(
      () =>
        (
          props.messages ??
          []
        ).map((row) => ({
          ...row,

          sourceModule:
            row.sourceModule ??
            row
              .linkedEntityType ??
            "MESSAGE",

          workType:
            "Message",

          title:
            workItemTitle(row),

          priority:
            row.priority ??
            row.severity ??
            "Medium",
        })),

      [props.messages],
    );

  const allRows =
    useMemo(
      () => [
        ...taskRows,
        ...notificationRows,
        ...messageRows,
      ],

      [
        taskRows,
        notificationRows,
        messageRows,
      ],
    );

  const openTasks =
    taskRows
      .filter(
        isOpenTask,
      )
      .length;

  const overdueTasks =
    taskRows
      .filter(
        isOverdue,
      )
      .length;

  const unreadNotifications =
    notificationRows
      .filter(
        isUnread,
      )
      .length;

  const unreadMessages =
    messageRows
      .filter(
        isUnread,
      )
      .length;

  const columns =
    useMemo<
      readonly CmtsGridColumn<
        WorkRecord
      >[]
    >(
      () => [
        {
          id: "title",
          header:
            "Work item",
          sortable: true,
          filterable: true,
          minWidth: 250,

          render: (row) => (
            <div className="cmts-workspace-item">
              <strong>
                {workItemTitle(
                  row,
                )}
              </strong>

              <small>
                {displayLabel(
                  text(
                    row,
                    [
                      "workType",
                    ],
                    "Work",
                  ),
                )}
              </small>
            </div>
          ),
        },

        {
          id:
            "sourceModule",

          header:
            "Module",

          sortable:
            true,

          filterable:
            true,

          minWidth: 155,

          render: (row) => {
            const module = moduleValue(row);
            return (
              <span className={`cmts-workspace-module cmts-workspace-module--${normalizeCode(module)}`}>
                {module}
              </span>
            );
          },
        },

        {
          id:
            "status",

          header:
            "Status",

          sortable:
            true,

          filterable:
            true,

          minWidth:
            150,

          render: (row) => {
            const status =
              taskStatus(row);

            return (
              <span
                className={[
                  "cmts-status-chip",
                  `cmts-status-chip--${normalizeCode(
                    status,
                  )}`,
                ].join(" ")}
              >
                {displayLabel(
                  status,
                )}
              </span>
            );
          },
        },

        {
          id:
            "priority",

          header:
            "Priority",

          sortable:
            true,

          filterable:
            true,

          minWidth:
            135,

          render: (row) => {
            const priority =
              priorityValue(
                row,
              );

            return (
              <span
                className={[
                  "cmts-priority-chip",
                  `cmts-priority-chip--${normalizeCode(
                    priority,
                  )}`,
                ].join(" ")}
              >
                {displayLabel(
                  priority,
                )}
              </span>
            );
          },
        },

        {
          id:
            "dueAt",

          header:
            "Due / Created",

          sortable:
            true,

          filterable:
            true,

          minWidth:
            220,

          render: (row) => (
            <span className="cmts-workspace-date">
              {formatWorkDate(
                row,
              )}
            </span>
          ),
        },

        {
          id:
            "linkedEntityId",

          header:
            "Linked record",

          sortable:
            true,

          filterable:
            true,

          minWidth:
            195,

          render: (row) => (
            <div className="cmts-workspace-linked-record">
              <strong>
                {linkedRecordLabel(
                  row,
                  complaintOptions,
                )}
              </strong>

            </div>
          ),
        },

        {
          id:
            "workspaceActions",

          header:
            "Actions",

          sortable:
            false,

          filterable:
            false,

          minWidth:
            125,

          render: (row) => (
            <button
              type="button"
              className="cmts-notification-actions-button"
              aria-label="Open work item actions"
              onClick={(event) => {
                event
                  .stopPropagation();

                setActionsFor(
                  row,
                );
              }}
            >
              <span
                className="cmts-notification-actions-button__icon"
                aria-hidden="true"
              >
                ⋮
              </span>

              <span>
                Actions
              </span>
            </button>
          ),
        },
      ],

      [complaintOptions],
    );

  return (
    <PageContainer
      title="My Work"
      subtitle="Personal command center for tasks, notifications, messages and linked workflow actions."
    >
      <CmtsPageHintPanel
        defaultOpen={false}
      >
        Counts and work items are loaded from backend task,
        notification and message repositories using the current
        user&apos;s role and tenant scope.
      </CmtsPageHintPanel>

      <section className="cmts-workspace-hero">
        <div>
          <p className="cmts-section-eyebrow">
            Role-based command center
          </p>

          <h2>
            Today&apos;s CMTS priorities
          </h2>

          <p>
            Review due work, unread messages, approvals and linked
            complaint actions from one operational workspace.
          </p>
        </div>

        <div className="cmts-page-actions">
          <Link
            className="cmts-action-button"
            to="/cmts/complaints/new"
          >
            Create complaint
          </Link>

          <Button
            intent="button"
            className="cmts-secondary-button"
            disabled={
              props.busy
            }
            onClick={
              props.onRefresh
            }
          >
            {props.busy
              ? "Refreshing..."
              : "Refresh workload"}
          </Button>
        </div>
      </section>

      <section
        className="cmts-summary-grid"
        aria-label="My Work summary"
      >
        <article className="cmts-workspace-summary-card cmts-workspace-summary-card--tasks">
          <span>
            Open tasks
          </span>

          <strong>
            {openTasks}
          </strong>

          <small>
            Complaint, RCA, CAPA and verification actions
          </small>
        </article>

        <article className="cmts-workspace-summary-card cmts-workspace-summary-card--overdue">
          <span>
            Overdue tasks
          </span>

          <strong>
            {overdueTasks}
          </strong>

          <small>
            Open tasks that have crossed their due date
          </small>
        </article>

        <article className="cmts-workspace-summary-card cmts-workspace-summary-card--notifications">
          <span>
            Unread notifications
          </span>

          <strong>
            {unreadNotifications}
          </strong>

          <small>
            Workflow, SLA and system alerts awaiting review
          </small>
        </article>

        <article className="cmts-workspace-summary-card cmts-workspace-summary-card--messages">
          <span>
            Unread messages
          </span>

          <strong>
            {unreadMessages}
          </strong>

          <small>
            Complaint and cross-functional discussion threads
          </small>
        </article>
      </section>

      <CmtsSmartDataGrid<WorkRecord>
        title="My unified work queue"
        subtitle="Tasks, notifications and messages with linked record actions."
        rows={
          allRows
        }
        columns={
          columns
        }
        rowKey="id"
        defaultPageSize={10}
        refreshable
        onRefresh={
          props.onRefresh
        }
        exportable
        loading={
          props.busy
        }
        filters={[
          {
            id:
              "workType",

            label:
              "Work type",

            columnId:
              "workType",

            operator:
              "equals",

            placeholder:
              "All work",
          },

          {
            id:
              "status",

            label:
              "Status",

            columnId:
              "status",

            operator:
              "contains",

            placeholder:
              "All statuses",
          },

          {
            id:
              "sourceModule",

            label:
              "Source module",

            columnId:
              "sourceModule",

            operator:
              "contains",

            placeholder:
              "All modules",
          },
        ]}
        emptyMessage="No assigned work, unread notifications or message threads are visible for this user."
      />

      <CmtsEnterpriseModal
        open={
          Boolean(
            actionsFor,
          )
        }
        title="Work item actions"
        subtitle={
          actionsFor
            ? workItemTitle(
                actionsFor,
              )
            : ""
        }
        size="md"
        onClose={() =>
          setActionsFor(
            null,
          )
        }
      >
        {actionsFor ? (
          <div className="cmts-workspace-action-list">
            <button
              type="button"
              className="cmts-workspace-action-option"
              onClick={async () => {
                const row =
                  actionsFor;

                setActionsFor(
                  null,
                );

                if (
                  row.workType ===
                  "Task"
                ) {
                  await props
                    .onOpenTask(
                      row as EnterpriseTask,
                    );
                } else if (
                  row.workType ===
                  "Notification"
                ) {
                  await props
                    .onOpenNotification(
                      row as EnterpriseNotification,
                    );
                } else {
                  await props
                    .onOpenMessage(
                      row,
                    );
                }
              }}
            >
              <strong>
                Open linked record
              </strong>

              <span>
                View the related complaint or workflow record.
              </span>
            </button>

            {actionsFor.workType ===
              "Task" &&
            isOpenTask(
              actionsFor,
            ) ? (
              <button
                type="button"
                className="cmts-workspace-action-option"
                onClick={async () => {
                  const row =
                    actionsFor;

                  setActionsFor(
                    null,
                  );

                  await props
                    .onCompleteTask(
                      row as EnterpriseTask,
                    );
                }}
              >
                <strong>
                  Complete task
                </strong>

                <span>
                  Complete this assigned work item.
                </span>
              </button>
            ) : null}

            {actionsFor.workType ===
              "Notification" &&
            isUnread(
              actionsFor,
            ) ? (
              <button
                type="button"
                className="cmts-workspace-action-option"
                onClick={async () => {
                  const row =
                    actionsFor;

                  setActionsFor(
                    null,
                  );

                  await props
                    .onMarkRead(
                      row as EnterpriseNotification,
                    );
                }}
              >
                <strong>
                  Mark as read
                </strong>

                <span>
                  Mark this notification as reviewed.
                </span>
              </button>
            ) : null}

            {actionsFor.workType ===
              "Message" &&
            props.onMarkMessageRead &&
            isUnread(
              actionsFor,
            ) ? (
              <button
                type="button"
                className="cmts-workspace-action-option"
                onClick={async () => {
                  const row =
                    actionsFor;

                  setActionsFor(
                    null,
                  );

                  await props
                    .onMarkMessageRead?.(
                      row,
                    );
                }}
              >
                <strong>
                  Mark message read
                </strong>

                <span>
                  Mark this message thread as reviewed.
                </span>
              </button>
            ) : null}

            {props.onOpenAuditTrail &&
            text(
              actionsFor,
              [
                "complaintId",
                "parentComplaintId",
                "linkedEntityId",
              ],
            ) ? (
              <button
                type="button"
                className="cmts-workspace-action-option"
                onClick={() => {
                  const row =
                    actionsFor;

                  setActionsFor(
                    null,
                  );

                  props
                    .onOpenAuditTrail?.(
                      row,
                    );
                }}
              >
                <strong>
                  Audit trail
                </strong>

                <span>
                  Review the linked record audit history.
                </span>
              </button>
            ) : null}

            {props.onOpenOperationSpine &&
            text(
              actionsFor,
              [
                "complaintId",
                "parentComplaintId",
                "linkedEntityId",
              ],
            ) ? (
              <button
                type="button"
                className="cmts-workspace-action-option"
                onClick={() => {
                  const row =
                    actionsFor;

                  setActionsFor(
                    null,
                  );

                  props
                    .onOpenOperationSpine?.(
                      row,
                    );
                }}
              >
                <strong>
                  Operation spine
                </strong>

                <span>
                  View workflow and operational traceability.
                </span>
              </button>
            ) : null}
          </div>
        ) : null}
      </CmtsEnterpriseModal>
    </PageContainer>
  );
}